import { useState } from "react";
import { Share2, Copy, Check, EyeOff, ExternalLink, QrCode, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { shareSetlist, unshareSetlist } from "@/lib/api";

export default function ShareButton({ setlist, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const publicUrl = setlist?.public_slug
    ? `${window.location.origin}/share/${setlist.public_slug}`
    : "";

  const handleEnable = async () => {
    setBusy(true);
    try {
      const updated = await shareSetlist(setlist.id);
      onUpdate(updated);
      toast.success("Link público gerado");
    } catch {
      toast.error("Erro ao gerar link");
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    setBusy(true);
    try {
      const updated = await unshareSetlist(setlist.id);
      onUpdate(updated);
      toast.success("Compartilhamento desativado");
    } catch {
      toast.error("Erro ao desativar");
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Link copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  };

  const handleDownloadQr = () => {
    const canvas = document.querySelector('canvas[data-testid="public-qr-canvas"]');
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `${setlist?.public_slug || "setlist"}-qr.png`;
    link.click();
    toast.success("QR code baixado");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`bg-transparent border-[#2D2D2D] text-white hover:bg-[#1A1A1A] ${
            setlist?.is_public ? "border-[#00F0FF] text-[#00F0FF]" : ""
          }`}
          data-testid="share-setlist-btn"
        >
          <Share2 className="w-4 h-4 mr-2" />
          {setlist?.is_public ? "Compartilhado" : "Compartilhar"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            Compartilhar setlist
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <p className="text-sm text-zinc-400 leading-relaxed">
            Gere um link público em modo somente-leitura. Perfeito para enviar
            seu repertório a contratantes, casas de show ou compartilhar nas
            redes — sem precisar de login.
          </p>

          {!setlist?.is_public ? (
            <Button
              onClick={handleEnable}
              disabled={busy}
              className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold"
              data-testid="enable-public-link-btn"
            >
              <Share2 className="w-4 h-4 mr-2" /> Gerar link público
            </Button>
          ) : (
            <>
              <div
                className="flex items-stretch gap-2 rounded-md border border-[#2D2D2D] bg-[#121212] p-2"
                data-testid="public-link-box"
              >
                <input
                  readOnly
                  value={publicUrl}
                  data-testid="public-link-input"
                  className="flex-1 bg-transparent font-mono text-xs sm:text-sm text-[#00F0FF] outline-none px-2 truncate"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  size="sm"
                  onClick={handleCopy}
                  className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold h-9"
                  data-testid="copy-public-link-btn"
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => setShowQr((v) => !v)}
                  variant="outline"
                  className="flex-1 bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]"
                  data-testid="toggle-qr-btn"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  {showQr ? "Esconder QR" : "Mostrar QR"}
                </Button>
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="open-public-link"
                >
                  <Button
                    variant="outline"
                    className="w-full bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" /> Abrir
                  </Button>
                </a>
              </div>

              {showQr && (
                <div
                  className="rounded-md border border-[#2D2D2D] bg-white p-4 flex flex-col items-center gap-3"
                  data-testid="qr-panel"
                >
                  <QRCodeCanvas
                    value={publicUrl}
                    size={192}
                    bgColor="#FFFFFF"
                    fgColor="#000000"
                    level="M"
                    includeMargin={false}
                    data-testid="public-qr-canvas"
                  />
                  <Button
                    size="sm"
                    onClick={handleDownloadQr}
                    className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold"
                    data-testid="download-qr-btn"
                  >
                    <Download className="w-4 h-4 mr-2" /> Baixar PNG
                  </Button>
                  <p className="text-[11px] text-zinc-700 font-mono text-center max-w-[14rem]">
                    Imprima no flyer ou cole no camarim — escaneie e pronto.
                  </p>
                </div>
              )}

              <Button
                onClick={handleDisable}
                disabled={busy}
                variant="outline"
                className="w-full bg-transparent border-red-900 text-red-400 hover:bg-red-950"
                data-testid="disable-public-link-btn"
              >
                <EyeOff className="w-4 h-4 mr-2" /> Desativar link
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
